import { ApiUserActionUser } from "@incendium/api";
import { Clear, FilterAlt } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { CenterPage } from "Components/CenterPage/CenterPage";
import SpacedList, { SpacedLinkButton } from "Components/UI/SpacedList";
import { friendlyDateTime } from "Helpers/dates";
import useProjectTimezone from "Hooks/useProjectTimezone";
import ProviderIcon from "features/attributionAnalytics/components/ProviderIcon";
import {
  LeadTimelineLIstFilterDialog,
  useLeadAnalyticsData,
  useLeadFilters,
} from "features/leadAnalytics";
import { Pagination, usePagination } from "features/pagination";
import { useCallback, useEffect } from "react";
import { useState } from "react";
import { useUpdateEffect } from "react-use";

interface ILeadTimelineListProps {
  onClick: (l: ApiUserActionUser, page: number) => void;
  initialPage?: number;
  useParamsPagination?: boolean;
  leadIds?: number[];
}

function LeadTimelineList({
  onClick,
  initialPage,
  useParamsPagination,
  leadIds,
}: ILeadTimelineListProps) {
  // preload available filters,
  useLeadFilters();
  const timezone = useProjectTimezone();

  const [open, setOpen] = useState(false);
  const [tmpSearch, setTmpSearch] = useState("");
  const { token, totalPages, page, setPage, setReponseToken, resetPagination } =
    usePagination({
      limit: 20,
      useParams: useParamsPagination,
      initialPage,
    });

  const { chartData, loading, setSearch, setFilters, filters, setLeadIds } =
    useLeadAnalyticsData(token);
  useEffect(() => {
    if (leadIds) {
      setLeadIds(leadIds);
    } else {
      setLeadIds([]);
    }
    return () => {
      setLeadIds([]);
    };
  }, [leadIds, setLeadIds]);

  useEffect(() => {
    setReponseToken(chartData?.nextPageToken || "");
  }, [chartData, setReponseToken]);

  const doSearch = useCallback(() => {
    resetPagination();
    setSearch(tmpSearch);
  }, [tmpSearch, setSearch, resetPagination]);

  useUpdateEffect(() => {
    if (filters) {
      resetPagination();
    }
  }, [filters]);

  useUpdateEffect(() => {
    if (tmpSearch === "") {
      doSearch();
    }
  }, [tmpSearch]);

  return (
    <Stack spacing={2}>
      <LeadTimelineLIstFilterDialog
        open={open}
        setOpen={setOpen}
        filters={filters}
        setFilters={setFilters}
      />

      <Box>
        {loading ? (
          <CenterPage calcHeight>
            <CircularProgress />
            <Typography variant="h3" mt={2}>
              Fetching User Data
            </Typography>
          </CenterPage>
        ) : (
          <SpacedList
            title={
              <Pagination
                totalPages={totalPages}
                page={page}
                onChange={(p) => setPage(p)}
              />
            }
            action={
              <Stack direction={"row"} spacing={3}>
                <Stack direction={"row"} spacing={1}>
                  <TextField
                    size="small"
                    label="Search by Email or Key."
                    value={tmpSearch}
                    onChange={(e) => setTmpSearch(e.target.value)}
                    onKeyPress={(ev) => {
                      if (ev.key === "Enter") {
                        ev.preventDefault();
                        doSearch();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <Box mr={1}>
                          <IconButton
                            onClick={() => {
                              setTmpSearch("");
                            }}
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        </Box>
                      ),
                    }}
                  />
                  <Button onClick={doSearch}>Search</Button>
                </Stack>
                <Button
                  startIcon={<FilterAlt />}
                  onClick={() => setOpen(!open)}
                >
                  Select Filters
                </Button>
              </Stack>
            }
          >
            <TableHead>
              <TableRow>
                <TableCell>Email / Key</TableCell>
                <TableCell sx={{ minWidth: 170 }}>Last Session</TableCell>
                <TableCell>First Channel - Source / Platform</TableCell>
                <TableCell>Lead Audiences</TableCell>
                <TableCell>Lead Score</TableCell>
                <TableCell>Lead Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(chartData?.users || []).map((l) => (
                <TableRow key={l.key}>
                  <TableCell>
                    <SpacedLinkButton onClick={() => onClick(l, page)}>
                      {l.email ? l.email : l.key || "N/A"}
                    </SpacedLinkButton>
                  </TableCell>
                  <TableCell>
                    {friendlyDateTime(l.lastActivity || "", timezone)}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ProviderIcon provider={l.firstSource || ""} />
                      <span>
                        {l.firstChannel} - {l.firstSource}
                      </span>
                    </Stack>
                  </TableCell>
                  <TableCell> {(l.audiences || []).join(", ")}</TableCell>
                  <TableCell> {l.score}</TableCell>
                  <TableCell>{l.leadStatusRuleName}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </SpacedList>
        )}
      </Box>
    </Stack>
  );
}

export default LeadTimelineList;
